import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    UserPlus,
    Trash2,
    Loader2,
    Mail,
    User as UserIcon,
    KeyRound,
    Shield,
    Users,
} from "lucide-react";
import api, { formatApiError } from "./api";

export default function PortalAdmin() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        email: "",
        password: "",
        name: "",
        role: "employee",
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data || []);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const create = async (e) => {
        e.preventDefault();
        if (creating) return;
        setCreating(true);
        try {
            const payload = {
                email: form.email.trim().toLowerCase(),
                password: form.password,
                name: form.name.trim(),
                role: form.role,
            };
            await api.post("/admin/users", payload);
            toast.success("Employee invited", {
                description: `${payload.email} can now sign in.`,
            });
            setForm({ email: "", password: "", name: "", role: "employee" });
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create user");
        } finally {
            setCreating(false);
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Remove ${u.email}?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User removed");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to delete user");
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="admin-page"
        >
            <div className="pr-glass-strong rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center">
                        <UserPlus className="w-5 h-5 text-emerald-300" />
                    </div>
                    <div>
                        <h1 className="font-outfit text-2xl font-bold text-white">
                            Invite an employee
                        </h1>
                        <p className="text-sm text-slate-400">
                            Pre-invited only — share their email + password with
                            them privately.
                        </p>
                    </div>
                </div>

                <form onSubmit={create} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="invite-form">
                    <Field
                        icon={<UserIcon className="w-4 h-4 text-blue-300" />}
                        label="Full name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Alex Doe"
                        required
                        testId="invite-name"
                    />
                    <Field
                        icon={<Mail className="w-4 h-4 text-blue-300" />}
                        label="Work email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="alex@proksyresearch.com"
                        required
                        testId="invite-email"
                    />
                    <Field
                        icon={<KeyRound className="w-4 h-4 text-blue-300" />}
                        label="Initial password"
                        type="text"
                        value={form.password}
                        onChange={set("password")}
                        placeholder="min 8 chars"
                        required
                        testId="invite-password"
                    />
                    <div>
                        <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                            Role
                        </label>
                        <select
                            value={form.role}
                            onChange={set("role")}
                            className="input-glow w-full rounded-xl py-3 px-4 text-sm text-white"
                            data-testid="invite-role"
                        >
                            <option value="employee">Employee</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <button
                            type="submit"
                            disabled={creating}
                            className="btn-primary disabled:opacity-50"
                            data-testid="invite-submit"
                        >
                            {creating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <UserPlus className="w-4 h-4" />
                            )}
                            Send invitation
                        </button>
                    </div>
                </form>
            </div>

            <div className="pr-glass rounded-2xl p-6 sm:p-7">
                <div className="flex items-center gap-3 mb-5">
                    <Users className="w-4 h-4 text-blue-300" />
                    <h2 className="font-outfit text-lg font-semibold text-white">
                        Team
                    </h2>
                    <span className="text-[11px] font-mono-pr text-slate-500">
                        {users.length} member{users.length !== 1 ? "s" : ""}
                    </span>
                </div>

                {loading ? (
                    <div className="text-sm text-slate-400 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="user-list">
                        {users.map((u) => (
                            <li
                                key={u.id}
                                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5"
                                data-testid={`user-row-${u.email}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div
                                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            u.role === "admin"
                                                ? "bg-emerald-500/10 border border-emerald-400/30"
                                                : "bg-blue-500/10 border border-blue-400/30"
                                        }`}
                                    >
                                        {u.role === "admin" ? (
                                            <Shield className="w-4 h-4 text-emerald-300" />
                                        ) : (
                                            <UserIcon className="w-4 h-4 text-blue-300" />
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm text-white truncate font-outfit">
                                            {u.name || u.email}
                                        </p>
                                        <p className="text-xs text-slate-500 truncate font-mono-pr">
                                            {u.email} · {u.role}
                                            {u.mailbox?.imap_password_set
                                                ? " · mailbox connected"
                                                : ""}
                                        </p>
                                    </div>
                                </div>
                                {u.role !== "admin" && (
                                    <button
                                        onClick={() => remove(u)}
                                        className="w-8 h-8 rounded-lg pr-glass flex items-center justify-center text-slate-400 hover:text-rose-300 hover:border-rose-400/40 border border-transparent"
                                        title="Remove user"
                                        data-testid={`user-remove-${u.email}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </motion.div>
    );
}

function Field({ icon, label, type = "text", value, onChange, placeholder, required, testId }) {
    return (
        <div>
            <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                {label}
            </label>
            <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {icon}
                </span>
                <input
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    className="input-glow w-full rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500"
                    data-testid={testId}
                />
            </div>
        </div>
    );
}
